/**
 * DealDetailPanel (SPEC E3 — pipeline-and-capi, R2 / amendment E3-H4).
 *
 * A side panel (not a new page) opened by clicking a kanban DealCard. Shows the
 * opportunity fields, contact context, stage history, and CAPI attribution, and
 * lets the operator edit opportunity fields via PUT /api/opportunities/{id}.
 * Implemented as a fixed right-side drawer (no extra Sheet dependency).
 */

import { useEffect, useState } from 'react';
import { X, CalendarPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { updateOpportunity } from '@/lib/api/pipeline';
import { getTenantConfig } from '@/lib/api/tenant';
import { BookOnBehalfModal } from '@/components/BookOnBehalfModal';
import { formatValue } from './DealCard';
import type { Opportunity } from '@/types/pipeline';

interface DealDetailPanelProps {
  opportunity: Opportunity | null;
  open: boolean;
  onClose: () => void;
  onSaved: (updated: Opportunity) => void;
}

export function DealDetailPanel({
  opportunity,
  open,
  onClose,
  onSaved,
}: DealDetailPanelProps) {
  const [value, setValue] = useState<string>('');
  const [nextSteps, setNextSteps] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [saving, setSaving] = useState(false);
  // SPEC G1 R2 — book-on-behalf modal state + the tenant's Cal.com embed link.
  const [bookOpen, setBookOpen] = useState(false);
  const [calEmbedUrl, setCalEmbedUrl] = useState<string | null>(null);

  useEffect(() => {
    if (opportunity) {
      setValue(
        opportunity.value !== null && opportunity.value !== undefined
          ? String(opportunity.value)
          : ''
      );
      setNextSteps(opportunity.next_steps || '');
      setNotes(opportunity.qualification_notes || '');
    }
  }, [opportunity]);

  // Fetch the tenant's Cal.com embed link once when the panel opens (best-effort;
  // a missing link is handled by the modal's "not configured" message).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    getTenantConfig()
      .then((cfg) => {
        if (!cancelled) setCalEmbedUrl(cfg.cal_embed_url ?? null);
      })
      .catch(() => {
        if (!cancelled) setCalEmbedUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open || !opportunity) return null;

  const attribution = (opportunity.meta_attribution || {}) as Record<string, unknown>;

  const handleSave = async () => {
    if (!opportunity) return;
    setSaving(true);
    try {
      const updates: Partial<Opportunity> = {
        next_steps: nextSteps,
        qualification_notes: notes,
      };
      const parsed = value.trim() === '' ? null : Number(value);
      if (parsed !== null && Number.isNaN(parsed)) {
        toast.error('Deal value must be a number');
        setSaving(false);
        return;
      }
      updates.value = parsed;
      const updated = await updateOpportunity(opportunity.id, updates);
      toast.success('Deal updated');
      onSaved(updated);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update deal');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
        data-testid="deal-panel-backdrop"
      />
      {/* Drawer */}
      <aside
        className="fixed top-0 right-0 h-full w-[380px] max-w-[90vw] bg-card border-l shadow-xl z-50 flex flex-col"
        role="dialog"
        aria-label="Deal detail"
        data-testid="deal-detail-panel"
      >
        <header className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="text-base font-semibold">Deal detail</h2>
          <div className="flex items-center gap-1">
            {/* SPEC G1 R2 — book-on-behalf shortcut. Rendered only when the
                contact has an email (Cal.com pre-fill needs an email). */}
            {opportunity.email && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setBookOpen(true)}
                aria-label="Book on behalf"
              >
                <CalendarPlus className="h-4 w-4 mr-1" />
                Book
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Contact context */}
          <section className="space-y-1">
            <p className="text-sm font-medium">
              {opportunity.contact_name || 'Unknown contact'}
            </p>
            {opportunity.company_name && (
              <p className="text-xs text-muted-foreground">
                {opportunity.company_name}
              </p>
            )}
            {opportunity.email && (
              <p className="text-xs text-muted-foreground">{opportunity.email}</p>
            )}
            {opportunity.phone && (
              <p className="text-xs text-muted-foreground">{opportunity.phone}</p>
            )}
            <div className="flex items-center gap-2 pt-1">
              <Badge variant="secondary">{opportunity.stage_key}</Badge>
              <span className="text-xs text-muted-foreground">
                {formatValue(opportunity.value, opportunity.currency || 'CAD')}
              </span>
            </div>
          </section>

          {/* Editable fields */}
          <section className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="deal-value">Deal value</Label>
              <Input
                id="deal-value"
                inputMode="decimal"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="e.g. 5000"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="deal-next-steps">Next steps</Label>
              <Textarea
                id="deal-next-steps"
                value={nextSteps}
                onChange={(e) => setNextSteps(e.target.value)}
                rows={2}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="deal-notes">Notes</Label>
              <Textarea
                id="deal-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
              />
            </div>
          </section>

          {/* Stage history */}
          {Array.isArray(opportunity.stage_history) &&
            opportunity.stage_history.length > 0 && (
              <section className="space-y-1">
                <h3 className="text-xs font-semibold uppercase text-muted-foreground">
                  Stage history
                </h3>
                <ul className="space-y-1">
                  {opportunity.stage_history.map((h, i) => (
                    <li key={i} className="text-xs text-muted-foreground">
                      <span className="font-medium">{h.stage_key}</span>
                      {h.changed_at && (
                        <span> — {new Date(h.changed_at).toLocaleString()}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}

          {/* CAPI attribution */}
          {Object.keys(attribution).length > 0 && (
            <section className="space-y-1">
              <h3 className="text-xs font-semibold uppercase text-muted-foreground">
                Attribution
              </h3>
              <dl className="text-xs space-y-0.5">
                {Object.entries(attribution).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2">
                    <dt className="text-muted-foreground">{k}</dt>
                    <dd className="truncate max-w-[200px]">{String(v)}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}
        </div>

        <footer className="px-4 py-3 border-t flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </footer>
      </aside>

      {/* SPEC G1 R2 — book-on-behalf modal. Closing it just dismisses the embed;
          the booking is confirmed by Cal.com's webhook (R1) and the deal reflects
          the updated appointment on the next pipeline poll/refresh. */}
      <BookOnBehalfModal
        open={bookOpen}
        onClose={() => setBookOpen(false)}
        contactName={opportunity.contact_name}
        contactEmail={opportunity.email}
        calEmbedUrl={calEmbedUrl}
      />
    </>
  );
}
